import { ScrollingModule } from '@angular/cdk/scrolling';
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeCs from '@angular/common/locales/cs';
import localeCsExtra from '@angular/common/locales/extra/cs';
import { NgModule, LOCALE_ID, ErrorHandler, inject, provideAppInitializer } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MAT_RIPPLE_GLOBAL_OPTIONS, RippleGlobalOptions } from '@angular/material/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy } from '@angular/router';
import { provideSvgIcons, provideSvgIconsConfig } from '@ngneat/svg-icon';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { PageNotFoundComponent } from './layout/_global-errors/page-not-found/page-not-found.component';
import { RpaNotConnectedComponent } from './layout/_global-errors/rpa-not-connected/rpa-not-connected.component';
import { ServerErrorComponent } from './layout/_global-errors/server-error/server-error.component';
import { AlertBarComponent } from './layout/alert-bar/alert-bar.component';
import { LayoutComponent } from './layout/layout.component';
import { LoadingScreenComponent } from './layout/loading-screen/loading-screen.component';
import { MenuComponent } from './layout/menu/menu.component';
import { JumpToModalComponent } from './shared/components/_modals/jump-to-modal/jump-to-modal.component';
import { NotificationSettingsModalComponent } from './shared/components/_modals/notification-settings-modal/notification-settings-modal.component';
import { CALENDAR_CONFIG, CalendarConfig } from './shared/components/calendar/calendar-interval-dropdown/calendar-interval-dropdown.component';
import { GlobalErrorHandler } from './shared/handlers/global-error.handler';
import { DateHelper } from './shared/helpers/date.helper';
import { FormatHelper } from './shared/helpers/format.helper';
import { HttpRequestInterceptor } from './shared/interceptors/http.request.interceptor';
import { ApiService } from './shared/services/api/api.service';
import { AppConfigurationService } from './shared/services/app-configuration.service';
import { LoggingService } from './shared/services/logging.service';
import { PopupService } from './shared/services/popup.service';
import { TenantsService } from './shared/services/tenants.service';
import { SchedulerCacheService } from './shared/services/scheduler-cache.service';
import { SharedModule } from './shared/shared.module';
import { CustomRouteReuseStrategy } from './shared/types/custom-route-reuse-strategy.type';
import { Data } from './shared/types/data.type';
import { Global } from './shared/types/global.type';
import { analyticsIcons } from './svg/analytics';
import { navigationIcons } from './svg/navigation';
import { sharedIcons } from './svg/shared';
import { ChangePasswordModalComponent } from './layout/change-password-modal/change-password-modal.component';
import { ChatComponent } from './layout/chat/chat.component';
import { chatIcon } from './svg/chat';
import { defaultIcon } from './svg/default';

registerLocaleData(localeCs, 'cs', localeCsExtra);
const globalRippleConfig: RippleGlobalOptions = {
  disabled: true,
  animation: {
    enterDuration: 0,
    exitDuration: 0,
  },
};

async function unregisterServiceWorker(): Promise<void> {
  try {
    if (navigator?.serviceWorker != null) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const r of registrations) {
        const result = await r.unregister();
        console.log('Service worker unregistered with result', result);
      }
    }

    if (window.caches != null) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map(k => window.caches.delete(k)));
    }
  } catch (error) {
    console.error('Service worker could not be unregistered', error);
  }
}

@NgModule({
  declarations: [
    AppComponent,
    LayoutComponent,
    LoadingScreenComponent,
    PageNotFoundComponent,
    ServerErrorComponent,
    JumpToModalComponent,
    MenuComponent,
    AlertBarComponent,
    NotificationSettingsModalComponent,
    RpaNotConnectedComponent,
    ChangePasswordModalComponent,
    ChatComponent,
  ],
  schemas: [],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    SharedModule,
    FormsModule,
    ReactiveFormsModule,
    ScrollingModule,
  ],
  providers: [
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: globalRippleConfig },
    provideSvgIcons([...sharedIcons, ...navigationIcons, ...analyticsIcons, chatIcon, defaultIcon]),
    provideSvgIconsConfig({
      // Optional default values
      missingIconFallback: defaultIcon,
      defaultSize: 'md',
      sizes: {
        xs: '10px',
        sm: '14px',
        md: '16px',
        lg: '24px',
        xl: '32px',
        xxl: '42px',
      },
    }),
    LoggingService,
    Data,
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: HTTP_INTERCEPTORS, useClass: HttpRequestInterceptor, multi: true },
    provideAppInitializer(() => {
      const initializerFn = (AppConfigurationService.initialize)(inject(ApiService), inject(AppConfigurationService), inject(LoggingService), inject(PopupService));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = (Global.initialize)(inject(LOCALE_ID));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = (FormatHelper.initializeLocale)(inject(LOCALE_ID));
      initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = (DateHelper.initializeLocale)(inject(LOCALE_ID));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      return unregisterServiceWorker();
    }),
    provideAppInitializer(() => {
      // Eagerly construct so their EventBus subscriptions (OrganizationReady / TenantsLoaded) are
      // wired before any route guard emits those events. EventBusService is a no-replay Subject, so
      // a service constructed lazily after the emit would miss it and never initialize. Load-bearing.
      inject(TenantsService);
      inject(SchedulerCacheService);
    }),
    { provide: CALENDAR_CONFIG, useFactory: (appConfig: AppConfigurationService): CalendarConfig => ({ show30DaysButton: !appConfig.isOnPremise }) as CalendarConfig, deps: [AppConfigurationService] },
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
